type Position = [number, number];
type Interval = [number, number];

// A visual overlap tolerance, deliberately smaller than the off-route metric.
const OVERLAP_METERS = 15;
const METERS_PER_DEGREE = (Math.PI * 6371000) / 180;

export function visiblePlannedRoute(
  planned: Position[],
  executed: Position[][],
): Position[][] {
  if (!executed.some((segment) => segment.length > 1)) return [planned];
  const longitudeScale =
    METERS_PER_DEGREE * Math.cos(((planned[0]?.[0] ?? 0) * Math.PI) / 180);
  const project = ([lat, lng]: Position): Position => [
    lng * longitudeScale,
    lat * METERS_PER_DEGREE,
  ];
  const edges = executed.flatMap((segment) =>
    segment.slice(1).map((point, index) => ({
      start: project(segment[index]),
      end: project(point),
    })),
  );
  const result: Position[][] = [];
  let current: Position[] = [];
  for (let index = 1; index < planned.length; index++) {
    const start = project(planned[index - 1]);
    const end = project(planned[index]);
    const delta: Position = [end[0] - start[0], end[1] - start[1]];
    const hidden: Interval[] = [];
    for (const edge of edges) {
      if (
        [0, 1].some(
          (axis) =>
            Math.max(start[axis], end[axis]) + OVERLAP_METERS <
              Math.min(edge.start[axis], edge.end[axis]) ||
            Math.min(start[axis], end[axis]) - OVERLAP_METERS >
              Math.max(edge.start[axis], edge.end[axis]),
        )
      )
        continue;
      const vector = [edge.end[0] - edge.start[0], edge.end[1] - edge.start[1]];
      const lengthSquared = vector[0] ** 2 + vector[1] ** 2;
      const relative = [start[0] - edge.start[0], start[1] - edge.start[1]];
      const projection = lengthSquared
        ? (relative[0] * vector[0] + relative[1] * vector[1]) / lengthSquared
        : 0;
      const slope = lengthSquared
        ? (delta[0] * vector[0] + delta[1] * vector[1]) / lengthSquared
        : 0;
      const breaks = [0, 1];
      if (slope) {
        for (const boundary of [0, 1]) {
          const t = (boundary - projection) / slope;
          if (t > 0 && t < 1) breaks.push(t);
        }
      }
      breaks.sort((a, b) => a - b);
      for (let part = 1; part < breaks.length; part++) {
        const low = breaks[part - 1];
        const high = breaks[part];
        const middleProjection = projection + slope * ((low + high) / 2);
        const clamped = Math.max(0, Math.min(1, middleProjection));
        const interior = middleProjection > 0 && middleProjection < 1;
        const offset = relative.map(
          (value, axis) =>
            value - vector[axis] * (interior ? projection : clamped),
        );
        const direction = delta.map(
          (value, axis) => value - (interior ? vector[axis] * slope : 0),
        );
        const a = direction[0] ** 2 + direction[1] ** 2;
        const b = 2 * (offset[0] * direction[0] + offset[1] * direction[1]);
        const c = offset[0] ** 2 + offset[1] ** 2 - OVERLAP_METERS ** 2;
        if (a < 1e-12) {
          if (c <= 0) hidden.push([low, high]);
          continue;
        }
        const discriminant = b ** 2 - 4 * a * c;
        if (discriminant <= 0) continue;
        const from = Math.max(low, (-b - Math.sqrt(discriminant)) / (2 * a));
        const to = Math.min(high, (-b + Math.sqrt(discriminant)) / (2 * a));
        if (from < to) hidden.push([from, to]);
      }
    }
    hidden.sort((a, b) => a[0] - b[0]);
    const interpolate = (t: number): Position => [
      planned[index - 1][0] + (planned[index][0] - planned[index - 1][0]) * t,
      planned[index - 1][1] + (planned[index][1] - planned[index - 1][1]) * t,
    ];
    const flush = () => {
      if (current.length > 1) result.push(current);
      current = [];
    };
    let cursor = 0;
    for (const [from, to] of hidden) {
      if (from > cursor) {
        if (!current.length) current.push(interpolate(cursor));
        current.push(interpolate(from));
      }
      flush();
      cursor = Math.max(cursor, to);
    }
    if (cursor < 1) {
      if (!current.length) current.push(interpolate(cursor));
      current.push(planned[index]);
    }
  }
  if (current.length > 1) result.push(current);
  return result;
}
